import React, { useEffect, useState, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowLeftIcon, CheckCircle2Icon, CheckIcon, CircleAlertIcon, CircleIcon, InfoIcon, MailIcon, MinusIcon, PencilIcon, PhoneIcon, PlusIcon, RotateCcwIcon, ScaleIcon } from 'lucide-react';
import { Button } from './Button';
import { Modal } from './Modal';
type OrderMaterial = {
  articleNumber: string;
  description: string;
  quantityPerLotKg: number;
};
type ScheduledOrder = {
  id: string;
  product: string;
  lots: number;
  materials: OrderMaterial[];
};
type OrderWorkflowProps = {
  order: ScheduledOrder;
  onCancel: () => void;
  initialLot?: number;
  initialWeighedMaterialIds?: string[];
};
export function OrderWorkflow({
  order,
  onCancel,
  initialLot = 1,
  initialWeighedMaterialIds = []
}: OrderWorkflowProps) {
  const prefersReducedMotion = useReducedMotion();
  const resumeLot = Math.min(Math.max(initialLot, 1), order.lots);
  const [scanMessage, setScanMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [currentLot, setCurrentLot] = useState(resumeLot);
  const [currentMaterialIndex, setCurrentMaterialIndex] = useState(() => getFirstOpenMaterialIndex(order.materials, initialWeighedMaterialIds));
  const [weighingStep, setWeighingStep] = useState<'start-instruction' | 'processing-instruction' | 'scan' | 'weigh'>('start-instruction');
  const [nextMaterialIndexAfterProcessing, setNextMaterialIndexAfterProcessing] = useState<number | null>(null);
  const [hasShownProcessingInstruction, setHasShownProcessingInstruction] = useState(false);
  const [isLotReviewOpen, setIsLotReviewOpen] = useState(false);
  const [isOrderCompletionFeedbackOpen, setIsOrderCompletionFeedbackOpen] = useState(false);
  const [currentWeight, setCurrentWeight] = useState(0);
  const [tareWeight, setTareWeight] = useState(0);
  const [isMaterialStatusOpen, setIsMaterialStatusOpen] = useState(false);
  const [isSupervisorCodeOpen, setIsSupervisorCodeOpen] = useState(false);
  const [supervisorCode, setSupervisorCode] = useState('');
  const [supervisorCodeError, setSupervisorCodeError] = useState('');
  const [isSupervisorApproved, setIsSupervisorApproved] = useState(false);
  const [isMaterialEditMode, setIsMaterialEditMode] = useState(false);
  const [isReweighing, setIsReweighing] = useState(false);
  const [isWeightChangeConfirmationOpen, setIsWeightChangeConfirmationOpen] = useState(false);
  const [isScaleServiceOpen, setIsScaleServiceOpen] = useState(false);
  const [materials, setMaterials] = useState(() => order.materials.map(material => ({
    ...material,
    isActive: true
  })));
  const [weighedMaterialIds, setWeighedMaterialIds] = useState<string[]>(initialWeighedMaterialIds);
  const [weighedMaterialWeights, setWeighedMaterialWeights] = useState<Record<string, number>>(() => Object.fromEntries(order.materials.filter(material => initialWeighedMaterialIds.includes(material.articleNumber)).map(material => [material.articleNumber, material.quantityPerLotKg])));
  const supervisorCodeInputRef = useRef<HTMLInputElement>(null);
  const supervisorCodeTriggerRef = useRef<HTMLButtonElement>(null);
  const saveChangesTriggerRef = useRef<HTMLButtonElement>(null);
  const scaleServiceTriggerRef = useRef<HTMLButtonElement>(null);
  const lotReviewListRef = useRef<HTMLUListElement>(null);
  const currentMaterial = materials[currentMaterialIndex];
  const targetWeight = currentMaterial?.quantityPerLotKg || 0;
  const displayWeight = currentWeight - tareWeight;
  const isWeightReached = Math.abs(displayWeight - targetWeight) < 0.1;
  const activeMaterialCount = materials.filter(material => material.isActive).length;
  const completedMaterialCount = materials.filter(material => material.isActive && weighedMaterialIds.includes(material.articleNumber)).length;
  const currentActivePosition = materials.filter((material, index) => material.isActive && index <= currentMaterialIndex).length;
  const lotReviewMaterials = materials.filter(material => material.isActive && weighedMaterialIds.includes(material.articleNumber));
  const changedMaterialQuantities = materials.flatMap((material, index) => {
    const originalMaterial = order.materials[index];
    if (!originalMaterial || material.quantityPerLotKg === originalMaterial.quantityPerLotKg) {
      return [];
    }
    return [{
      articleNumber: material.articleNumber,
      description: material.description,
      previousQuantityKg: originalMaterial.quantityPerLotKg,
      nextQuantityKg: material.quantityPerLotKg
    }];
  });
  useEffect(() => {
    const listElement = lotReviewListRef.current;
    const shouldAutoScroll = isLotReviewOpen && lotReviewMaterials.length > 3 && listElement;
    if (!shouldAutoScroll || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
    let animationFrameId: number | undefined;
    let startTimeoutId: number | undefined;
    let previousTimestamp = 0;
    let isPaused = false;
    let hasReachedEnd = false;
    function scrollList(timestamp: number) {
      if (isPaused || hasReachedEnd) {
        animationFrameId = undefined;
        return;
      }
      if (previousTimestamp) {
        const maximumScrollTop = listElement.scrollHeight - listElement.clientHeight;
        const nextScrollTop = Math.min(listElement.scrollTop + (timestamp - previousTimestamp) * 0.018, maximumScrollTop);
        listElement.scrollTop = nextScrollTop;
        hasReachedEnd = nextScrollTop >= maximumScrollTop;
      }
      previousTimestamp = timestamp;
      if (!hasReachedEnd) {
        animationFrameId = window.requestAnimationFrame(scrollList);
      }
    }
    function pauseScrolling() {
      isPaused = true;
      if (animationFrameId !== undefined) {
        window.cancelAnimationFrame(animationFrameId);
        animationFrameId = undefined;
      }
    }
    function resumeScrolling() {
      if (hasReachedEnd) return;
      isPaused = false;
      previousTimestamp = 0;
      if (animationFrameId === undefined) {
        animationFrameId = window.requestAnimationFrame(scrollList);
      }
    }
    listElement.scrollTop = 0;
    startTimeoutId = window.setTimeout(resumeScrolling, 900);
    listElement.addEventListener('mouseenter', pauseScrolling);
    listElement.addEventListener('mouseleave', resumeScrolling);
    listElement.addEventListener('focusin', pauseScrolling);
    listElement.addEventListener('touchstart', pauseScrolling, {
      passive: true
    });
    return () => {
      if (startTimeoutId !== undefined) {
        window.clearTimeout(startTimeoutId);
      }
      if (animationFrameId !== undefined) {
        window.cancelAnimationFrame(animationFrameId);
      }
      listElement.removeEventListener('mouseenter', pauseScrolling);
      listElement.removeEventListener('mouseleave', resumeScrolling);
      listElement.removeEventListener('focusin', pauseScrolling);
      listElement.removeEventListener('touchstart', pauseScrolling);
    };
  }, [currentLot, isLotReviewOpen, lotReviewMaterials.length]);
  useEffect(() => {
    if (!isOrderCompletionFeedbackOpen) {
      return;
    }
    const completionTimeoutId = window.setTimeout(() => {
      onCancel();
    }, 2400);
    return () => window.clearTimeout(completionTimeoutId);
  }, [isOrderCompletionFeedbackOpen, onCancel]);
  function handleSimulateScan(type: 'success' | 'error') {
    if (type === 'success') {
      setScanMessage({
        type: 'success',
        text: 'Rohstoff erfolgreich gescannt.'
      });
      window.setTimeout(() => {
        setScanMessage(null);
        setWeighingStep('weigh');
        setCurrentWeight(targetWeight);
      }, 2000);
      return;
    }
    setScanMessage({
      type: 'error',
      text: 'Falscher Rohstoff gescannt. Bitte prüfen.'
    });
    window.setTimeout(() => {
      setScanMessage(null);
    }, 3000);
  }
  function handleTare() {
    setTareWeight(currentWeight);
  }
  function handleZero() {
    setCurrentWeight(0);
    setTareWeight(0);
  }
  function handleConfirmWeight() {
    if (!isWeightReached || !currentMaterial) return;
    const nextMaterialIndex = materials.findIndex((material, index) => index > currentMaterialIndex && material.isActive);
    const shouldShowProcessingInstruction = !isReweighing && !hasShownProcessingInstruction;
    setWeighedMaterialIds(materialIds => materialIds.includes(currentMaterial.articleNumber) ? materialIds : [...materialIds, currentMaterial.articleNumber]);
    setWeighedMaterialWeights(materialWeights => ({
      ...materialWeights,
      [currentMaterial.articleNumber]: displayWeight
    }));
    setScanMessage({
      type: 'success',
      text: 'Gewicht erfolgreich erfasst.'
    });
    window.setTimeout(() => {
      setScanMessage(null);
      setCurrentWeight(0);
      setTareWeight(0);
      if (isReweighing) {
        setIsReweighing(false);
        setIsMaterialStatusOpen(true);
        return;
      }
      if (shouldShowProcessingInstruction) {
        setHasShownProcessingInstruction(true);
        setNextMaterialIndexAfterProcessing(nextMaterialIndex === -1 ? null : nextMaterialIndex);
        setWeighingStep('processing-instruction');
        return;
      }
      if (nextMaterialIndex !== -1) {
        setCurrentMaterialIndex(nextMaterialIndex);
        setWeighingStep('scan');
        return;
      }
      setIsLotReviewOpen(true);
    }, 2000);
  }
  function handleLotCompletion() {
    if (currentLot >= order.lots) {
      setIsOrderCompletionFeedbackOpen(true);
      return;
    }
    const firstActiveMaterialIndex = materials.findIndex(material => material.isActive);
    setCurrentLot(lot => lot + 1);
    setWeighedMaterialIds([]);
    setWeighedMaterialWeights({});
    setCurrentWeight(0);
    setTareWeight(0);
    setIsReweighing(false);
    if (firstActiveMaterialIndex === -1) {
      setIsLotReviewOpen(true);
      return;
    }
    setCurrentMaterialIndex(firstActiveMaterialIndex);
    setWeighingStep('scan');
    setIsLotReviewOpen(false);
  }
  function handleMaterialActiveChange(index: number, isActive: boolean) {
    const nextMaterials = materials.map((material, materialIndex) => materialIndex === index ? {
      ...material,
      isActive
    } : material);
    setMaterials(nextMaterials);
    if (index === currentMaterialIndex && !isActive) {
      const nextMaterialIndex = nextMaterials.findIndex((material, materialIndex) => materialIndex > currentMaterialIndex && material.isActive);
      if (nextMaterialIndex === -1) {
        setIsMaterialStatusOpen(false);
        setIsMaterialEditMode(false);
        setIsLotReviewOpen(true);
        return;
      }
      setCurrentMaterialIndex(nextMaterialIndex);
      setWeighingStep('scan');
      setCurrentWeight(0);
      setTareWeight(0);
    }
  }
  function handleMaterialQuantityChange(index: number, value: string) {
    const nextQuantity = Number(value.replace(',', '.'));
    if (!Number.isFinite(nextQuantity) || nextQuantity < 0) {
      return;
    }
    setMaterials(currentMaterials => currentMaterials.map((material, materialIndex) => materialIndex === index ? {
      ...material,
      quantityPerLotKg: nextQuantity
    } : material));
  }
  function handleReweighMaterial(index: number) {
    const material = materials[index];
    if (!material) return;
    setMaterials(currentMaterials => currentMaterials.map((current, materialIndex) => materialIndex === index ? {
      ...current,
      isActive: true
    } : current));
    setWeighedMaterialIds(materialIds => materialIds.filter(materialId => materialId !== material.articleNumber));
    setCurrentMaterialIndex(index);
    setWeighingStep('scan');
    setCurrentWeight(0);
    setTareWeight(0);
    setIsReweighing(true);
    setIsMaterialStatusOpen(false);
  }
  function saveMaterialChanges() {
    setIsWeightChangeConfirmationOpen(false);
    setIsMaterialEditMode(false);
    setIsMaterialStatusOpen(false);
    setSupervisorCode('');
    setSupervisorCodeError('');
  }
  function requestSaveMaterialChanges() {
    const hasWeightChanges = materials.some((material, index) => material.quantityPerLotKg !== order.materials[index]?.quantityPerLotKg);
    if (hasWeightChanges) {
      setIsWeightChangeConfirmationOpen(true);
      return;
    }
    saveMaterialChanges();
  }
  function closeWeightChangeConfirmation() {
    setIsWeightChangeConfirmationOpen(false);
    window.setTimeout(() => saveChangesTriggerRef.current?.focus(), 0);
  }
  function closeScaleService() {
    setIsScaleServiceOpen(false);
    window.setTimeout(() => scaleServiceTriggerRef.current?.focus(), 0);
  }
  function openSupervisorCode() {
    setSupervisorCode('');
    setSupervisorCodeError('');
    setIsSupervisorApproved(false);
    setIsSupervisorCodeOpen(true);
    window.setTimeout(() => supervisorCodeInputRef.current?.focus(), 0);
  }
  function closeSupervisorCode() {
    setIsSupervisorCodeOpen(false);
    setSupervisorCodeError('');
    supervisorCodeTriggerRef.current?.focus();
  }
  function handleSupervisorCodeChange(value: string) {
    setSupervisorCode(value.replace(/\D/g, '').slice(0, 6));
    if (supervisorCodeError) setSupervisorCodeError('');
  }
  function handleSupervisorApproval(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (supervisorCode.length !== 6) {
      setSupervisorCodeError('Bitte geben Sie einen sechsstelligen Supervisor-Code ein.');
      return;
    }
    setIsSupervisorApproved(true);
    window.setTimeout(() => {
      setIsSupervisorCodeOpen(false);
      setIsSupervisorApproved(false);
      setIsMaterialEditMode(true);
      supervisorCodeTriggerRef.current?.focus();
    }, 1100);
  }
  return <section className="mx-auto w-full max-w-[1540px] px-5 pb-2 pt-6 sm:px-8 sm:pb-3 sm:pt-8 md:px-10 lg:px-14 xl:px-20" data-id="element-308">
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      ease: 'easeOut'
    }} data-id="element-309">
        <div className="flex flex-col gap-5 border-b border-border pb-6 sm:flex-row sm:items-center sm:justify-between" data-id="element-310">
          <div className="flex min-w-0 items-center gap-4 sm:gap-5" data-id="element-311">
            <button type="button" onClick={onCancel} className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md border border-border bg-surface text-text-primary transition-colors hover:bg-brand-yellow-light focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green active:bg-brand-green" aria-label="Abbrechen und zurück zur Liste" data-id="element-312">
              <ArrowLeftIcon size={26} strokeWidth={1.9} aria-hidden="true" data-id="element-313" />
            </button>
            <h1 className="font-heading text-3xl leading-tight sm:text-4xl" data-id="element-314">
              {weighingStep === 'start-instruction' || weighingStep === 'processing-instruction' ? 'Produktionshinweis' : weighingStep === 'weigh' ? 'Verwiegung' : 'Rohstoff scannen'}
            </h1>
          </div>

          <div className="flex min-w-[280px] flex-col gap-2 rounded-xl border border-border bg-surface px-5 py-3 shadow-sm" data-id="element-315">
            <div className="flex items-center justify-between gap-4" data-id="element-316">
              <div className="flex min-w-0 items-center gap-3" data-id="element-317">
                <button type="button" onClick={() => setIsMaterialStatusOpen(true)} aria-label="Rohstoffübersicht anzeigen" aria-pressed={isMaterialStatusOpen} className="flex h-9 w-9 shrink-0 items-center justify-center bg-brand-yellow-light text-text-primary transition-colors hover:bg-brand-green focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green [clip-path:polygon(25%_6.7%,75%_6.7%,100%_50%,75%_93.3%,25%_93.3%,0_50%)]" data-id="element-318">
                  <InfoIcon size={18} strokeWidth={2.2} aria-hidden="true" data-id="element-319" />
                </button>

                <div className="flex flex-col" data-id="element-320">
                  <div className="text-[11px] font-semibold uppercase leading-tight tracking-wider text-text-secondary" data-id="element-321">
                    Lot {currentLot} von {order.lots}
                  </div>
                  <div className="text-[11px] font-semibold uppercase leading-tight tracking-wider text-text-secondary" data-id="element-322">
                    {isLotReviewOpen ? `${completedMaterialCount} von ${activeMaterialCount} verwogen` : `Rohstoff ${currentActivePosition} von ${activeMaterialCount}`}
                  </div>
                </div>
              </div>

              <div className="whitespace-nowrap font-heading text-lg text-text-primary" data-id="element-323">
                {activeMaterialCount > 0 ? Math.round(completedMaterialCount / activeMaterialCount * 100) : 100}
                %
              </div>
            </div>

            <div className="h-1.5 w-full overflow-hidden rounded-full border border-border bg-surface-subtle" data-id="element-324">
              <div className="h-full rounded-full bg-brand-green transition-all duration-500 ease-out" style={{
              width: `${activeMaterialCount > 0 ? completedMaterialCount / activeMaterialCount * 100 : 100}%`
            }} data-id="element-325" />
            </div>
          </div>
        </div>

        <AnimatePresence mode="wait" initial={false} data-id="element-326">
          {isMaterialStatusOpen ? <motion.section key="material-status" aria-labelledby="material-status-title" className="mx-auto mt-6 w-full max-w-5xl overflow-hidden rounded-2xl border border-border bg-surface shadow-sm sm:mt-8" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2,
          ease: 'easeOut'
        }} data-id="element-327">
              <div className="flex items-center justify-between gap-4 border-b border-border bg-surface-subtle px-5 py-5 sm:px-7 sm:py-6" data-id="element-328">
                <div className="min-w-0" data-id="element-329">
                  <h2 id="material-status-title" className="font-heading text-2xl leading-tight sm:text-3xl" data-id="element-330">
                    Rohstoffstatus
                  </h2>
                  <p className="mt-1 text-base text-text-secondary" data-id="element-331">
                    Lot {currentLot} von {order.lots} · {activeMaterialCount}{' '}
                    aktive Rohstoffe
                  </p>
                </div>

                {!isMaterialEditMode ? <button ref={supervisorCodeTriggerRef} type="button" onClick={openSupervisorCode} className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-text-primary transition-colors hover:bg-brand-yellow-light focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green" aria-label="Rohstoffstatus bearbeiten – Supervisor-Code erforderlich" aria-haspopup="dialog" aria-expanded={isSupervisorCodeOpen} data-id="element-332">
                    <PencilIcon size={21} strokeWidth={2} aria-hidden="true" data-id="element-333" />
                  </button> : null}
              </div>
              <ol className="space-y-3 p-5 sm:p-7" aria-label="Rohstoffe des Auftrags" data-id="element-334">
                {materials.map((material, index) => {
              const isCompleted = weighedMaterialIds.includes(material.articleNumber);
              const isCurrent = material.isActive && index === currentMaterialIndex;
              const statusLabel = isCompleted ? 'Verwogen' : !material.isActive ? 'Deaktiviert' : isCurrent ? 'Wird verwogen' : 'Ausstehend';
              return <li key={material.articleNumber} className={`flex flex-col gap-4 rounded-xl border p-4 sm:p-5 ${isCompleted ? 'border-success bg-surface' : !material.isActive ? 'border-border bg-surface-subtle opacity-70' : isCurrent ? 'border-brand-green bg-brand-yellow-light' : 'border-border bg-surface-subtle'}`} data-id="element-335">
                      <div className="flex min-w-0 items-center gap-4" data-id="element-336">
                        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${isCompleted ? 'bg-success text-surface' : isCurrent ? 'bg-brand-green text-text-primary' : 'border border-border bg-surface text-text-secondary'}`} aria-hidden="true" data-id="element-337">
                          {isCompleted ? <CheckIcon size={23} strokeWidth={2.5} data-id="element-338" /> : isCurrent ? <ScaleIcon size={22} strokeWidth={2} data-id="element-339" /> : <CircleIcon size={21} strokeWidth={1.8} data-id="element-340" />}
                        </div>

                        <div className="min-w-0" data-id="element-341">
                          <p className={`font-heading text-lg text-text-primary sm:text-xl ${!material.isActive ? 'line-through' : ''}`} data-id="element-342">
                            {material.articleNumber}
                          </p>
                          <p className={`mt-1 text-base text-text-secondary sm:text-lg ${!material.isActive ? 'line-through' : ''}`} data-id="element-343">
                            {material.description}
                          </p>
                          {!material.isActive ? <p className="mt-2 text-sm font-medium text-text-secondary" data-id="element-344">
                              Deaktiviert · wird nicht verwogen
                            </p> : null}
                        </div>
                      </div>

                      <div className="flex flex-col gap-4 border-t border-border pt-4 sm:flex-row sm:items-end sm:justify-between" data-id="element-345">
                        {material.isActive ? <span className={`w-fit rounded-full px-3 py-1 text-sm font-medium ${isCompleted ? 'bg-success text-surface' : isCurrent ? 'bg-brand-green text-text-primary' : 'border border-border bg-surface text-text-secondary'}`} data-id="element-346">
                            {statusLabel}
                          </span> : <span className="text-sm font-medium text-text-secondary" data-id="element-347">
                            Aus der Verwiegung ausgeschlossen
                          </span>}

                        {isMaterialEditMode ? <div className="grid w-full gap-3 sm:w-auto sm:grid-cols-[auto_minmax(150px,190px)] sm:items-end" data-id="element-348">
                            <label className="flex min-h-[56px] cursor-pointer items-center gap-3 rounded-lg border border-border bg-surface px-3 text-base text-text-primary" data-id="element-349">
                              <input type="checkbox" checked={material.isActive} onChange={event => handleMaterialActiveChange(index, event.target.checked)} className="h-5 w-5 accent-[var(--brand-green)]" data-id="element-350" />
                              Aktiv
                            </label>

                            <div data-id="element-351">
                              <span className="block text-xs font-medium uppercase tracking-wider text-text-secondary" data-id="element-352">
                                Soll pro Lot · kg
                              </span>
                              <div className="mt-1 grid min-h-[56px] grid-cols-[56px_minmax(0,1fr)_56px] overflow-hidden rounded-lg border border-border bg-surface" data-id="element-353">
                                <button type="button" onClick={() => handleMaterialQuantityChange(index, Math.max(0, material.quantityPerLotKg - 0.1).toFixed(1))} aria-label={`Sollmenge für ${material.articleNumber} um 0,1 Kilogramm verringern`} className="flex items-center justify-center border-r border-border text-text-primary transition-colors hover:bg-brand-yellow-light focus-visible:z-10 focus-visible:outline focus-visible:outline-4 focus-visible:outline-brand-green" data-id="element-354">
                                  <MinusIcon size={24} strokeWidth={2.4} aria-hidden="true" data-id="element-355" />
                                </button>
                                <input type="number" min="0" step="0.1" inputMode="decimal" value={material.quantityPerLotKg} onChange={event => handleMaterialQuantityChange(index, event.target.value)} aria-label={`Sollmenge für ${material.articleNumber} in Kilogramm pro Lot`} className="min-w-0 bg-surface px-2 text-center font-heading text-lg text-text-primary outline-none focus-visible:ring-4 focus-visible:ring-brand-green/25" data-id="element-356" />
                                <button type="button" onClick={() => handleMaterialQuantityChange(index, (material.quantityPerLotKg + 0.1).toFixed(1))} aria-label={`Sollmenge für ${material.articleNumber} um 0,1 Kilogramm erhöhen`} className="flex items-center justify-center border-l border-border bg-brand-yellow-light text-text-primary transition-colors hover:bg-brand-green focus-visible:z-10 focus-visible:outline focus-visible:outline-4 focus-visible:outline-brand-green" data-id="element-357">
                                  <PlusIcon size={24} strokeWidth={2.4} aria-hidden="true" data-id="element-358" />
                                </button>
                              </div>
                            </div>

                            {isCompleted ? <button type="button" onClick={() => handleReweighMaterial(index)} className="min-h-[48px] rounded-lg border border-border bg-surface px-4 font-heading text-base text-text-primary transition-colors hover:bg-brand-yellow-light focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green sm:col-span-2" data-id="element-359">
                                <span className="flex items-center justify-center gap-2" data-id="element-360">
                                  <RotateCcwIcon size={18} strokeWidth={2} aria-hidden="true" data-id="element-361" />
                                  Neu verwiegen
                                </span>
                              </button> : null}
                          </div> : isCompleted ? <div className="grid grid-cols-2 gap-5 sm:text-right" data-id="element-362">
                            <div data-id="element-363">
                              <span className="block text-xs font-medium uppercase tracking-wider text-text-secondary" data-id="element-364">
                                Soll
                              </span>
                              <span className="mt-1 block font-heading text-xl text-text-primary sm:text-2xl" data-id="element-365">
                                {material.quantityPerLotKg.toLocaleString('de-DE', {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1
                        })}{' '}
                                kg
                              </span>
                            </div>
                            <div data-id="element-366">
                              <span className="block text-xs font-medium uppercase tracking-wider text-text-secondary" data-id="element-367">
                                Ist
                              </span>
                              <span className="mt-1 block font-heading text-xl text-success sm:text-2xl" data-id="element-368">
                                {(weighedMaterialWeights[material.articleNumber] ?? material.quantityPerLotKg).toLocaleString('de-DE', {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1
                        })}{' '}
                                kg
                              </span>
                            </div>
                          </div> : <div className="sm:text-right" data-id="element-369">
                            <span className="block text-xs font-medium uppercase tracking-wider text-text-secondary" data-id="element-370">
                              Soll
                            </span>
                            <span className="mt-1 block font-heading text-xl text-text-primary sm:text-2xl" data-id="element-371">
                              {material.quantityPerLotKg.toLocaleString('de-DE', {
                        minimumFractionDigits: 1,
                        maximumFractionDigits: 1
                      })}{' '}
                              kg
                            </span>
                          </div>}
                      </div>
                    </li>;
            })}
              </ol>
              <div className="flex justify-end border-t border-border px-5 py-4 sm:px-7" data-id="element-372">
                <button ref={isMaterialEditMode ? saveChangesTriggerRef : undefined} type="button" onClick={isMaterialEditMode ? requestSaveMaterialChanges : () => setIsMaterialStatusOpen(false)} className="flex min-h-[56px] items-center rounded-lg bg-brand-green px-6 font-heading text-lg text-text-primary transition-colors hover:bg-brand-green-dark focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green" data-id="element-373">
                  {isMaterialEditMode ? 'Änderungen speichern' : 'Übersicht schließen'}
                </button>
              </div>
            </motion.section> : isLotReviewOpen ? <motion.section key={`lot-review-${currentLot}`} aria-labelledby="lot-review-title" className="mx-auto mt-6 w-full max-w-5xl overflow-hidden rounded-2xl border border-success bg-surface shadow-sm sm:mt-8" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2,
          ease: 'easeOut'
        }} data-id="element-374">
              <div className="flex items-center gap-3 border-b border-border bg-surface-subtle px-5 py-4 sm:px-6" data-id="element-375">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-success text-surface" data-id="element-376">
                  <CheckCircle2Icon size={23} strokeWidth={2.2} aria-hidden="true" data-id="element-377" />
                </div>
                <div className="min-w-0" data-id="element-378">
                  <h2 id="lot-review-title" className="font-heading text-xl leading-tight text-text-primary sm:text-2xl" data-id="element-379">
                    Lot {currentLot} vollständig verwogen
                  </h2>
                  <p className="mt-0.5 text-sm text-text-secondary" data-id="element-380">
                    Verwogene Rohstoffe prüfen und fortfahren.
                  </p>
                </div>
              </div>

              <div className="p-4 sm:p-5" data-id="element-381">
                {lotReviewMaterials.length > 0 ? <ul ref={lotReviewListRef} className={`space-y-1.5 ${lotReviewMaterials.length > 3 ? 'max-h-[320px] overflow-y-auto pr-1 [scrollbar-width:thin]' : ''}`} aria-label={`Verwogene Rohstoffe für Lot ${currentLot}`} data-id="element-382">
                    {lotReviewMaterials.map(material => {
                const actualWeight = weighedMaterialWeights[material.articleNumber] ?? material.quantityPerLotKg;
                return <li key={material.articleNumber} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 rounded-md border border-border bg-surface-subtle px-2.5 py-2 sm:gap-x-5" data-id="element-383">
                          <div className="min-w-0" data-id="element-384">
                            <p className="font-heading text-sm leading-tight text-text-primary" data-id="element-385">
                              {material.articleNumber}
                            </p>
                            <p className="mt-0.5 truncate text-xs leading-tight text-text-secondary" data-id="element-386">
                              {material.description}
                            </p>
                          </div>
                          <dl className="grid shrink-0 grid-cols-2 gap-x-3 text-right sm:gap-x-5" data-id="element-387">
                            <div data-id="element-388">
                              <dt className="text-[9px] font-medium uppercase leading-none tracking-wider text-text-secondary" data-id="element-389">
                                Soll
                              </dt>
                              <dd className="mt-1 whitespace-nowrap font-heading text-sm leading-none text-text-primary" data-id="element-390">
                                {formatKilograms(material.quantityPerLotKg)} kg
                              </dd>
                            </div>
                            <div data-id="element-391">
                              <dt className="text-[9px] font-medium uppercase leading-none tracking-wider text-text-secondary" data-id="element-392">
                                Ist
                              </dt>
                              <dd className="mt-1 whitespace-nowrap font-heading text-sm leading-none text-success" data-id="element-393">
                                {formatKilograms(actualWeight)} kg
                              </dd>
                            </div>
                          </dl>
                        </li>;
              })}
                  </ul> : <div className="rounded-lg border border-border bg-surface-subtle p-4 text-sm text-text-secondary" data-id="element-394">
                    Für dieses Lot sind keine aktiven Rohstoffe vorgesehen.
                  </div>}
              </div>

              <div className="flex flex-col gap-3 border-t border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7" data-id="element-395">
                <p className="text-sm text-text-secondary" data-id="element-396">
                  {lotReviewMaterials.length} von {activeMaterialCount} aktiven
                  Rohstoffen verwogen
                </p>
                <button type="button" onClick={handleLotCompletion} className="min-h-[60px] rounded-xl bg-brand-green px-7 font-heading text-lg text-text-primary transition-colors hover:bg-brand-green-dark focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green" data-id="element-397">
                  {currentLot < order.lots ? 'Nächstes Lot' : 'Auftrag abschließen'}
                </button>
              </div>
            </motion.section> : weighingStep === 'start-instruction' || weighingStep === 'processing-instruction' ? <motion.section key={`${weighingStep}-${currentLot}`} aria-labelledby="production-instruction-title" className="mx-auto mt-8 flex w-full max-w-[520px] flex-col items-center rounded-2xl border border-border bg-surface px-6 py-10 text-center shadow-md sm:mt-12 sm:px-12 sm:py-16" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: prefersReducedMotion ? 0 : 0.32,
          ease: 'easeOut'
        }} data-id="element-398">
              <div className="flex h-24 w-24 items-center justify-center bg-brand-yellow-light text-text-primary [clip-path:polygon(25%_6.7%,75%_6.7%,100%_50%,75%_93.3%,25%_93.3%,0_50%)]" aria-hidden="true" data-id="element-399">
                <InfoIcon size={54} strokeWidth={1.7} data-id="element-400" />
              </div>
              <div className="mt-9 w-full text-left" data-id="element-401">
                <h2 id="production-instruction-title" className="font-heading text-3xl leading-tight text-text-primary sm:text-4xl" data-id="element-402">
                  {weighingStep === 'start-instruction' ? 'Alle Zutaten auf Zimmertemperatur temperieren' : 'Mischen und gemeinsam durch den Fleischwolf lassen. Größe Lochscheibeneinsatz 8 mm, dann in den Kessel des Rührgerätes geben.'}
                </h2>
              </div>
              <Button type="button" size="lg" onClick={() => {
            if (weighingStep === 'start-instruction') {
              setWeighingStep('scan');
              return;
            }
            if (nextMaterialIndexAfterProcessing !== null) {
              setCurrentMaterialIndex(nextMaterialIndexAfterProcessing);
              setNextMaterialIndexAfterProcessing(null);
              setWeighingStep('scan');
              return;
            }
            setIsLotReviewOpen(true);
          }} className="mt-10 min-h-[64px] w-full justify-center text-base sm:text-lg" data-id="element-403">
                {weighingStep === 'start-instruction' ? 'Weiter zum Rohstoff-Scan' : nextMaterialIndexAfterProcessing !== null ? 'Weiter zum nächsten Rohstoff' : 'Weiter zur Lot-Übersicht'}
              </Button>
            </motion.section> : weighingStep === 'scan' ? <motion.div key="scan" className="mt-12 flex flex-col items-center justify-center pb-12" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2,
          ease: 'easeOut'
        }} data-id="element-404">
              <div className="mb-8 text-center" data-id="element-405">
                <h2 className="font-heading text-4xl sm:text-5xl" data-id="element-406">
                  {currentMaterial?.articleNumber}
                </h2>
                <p className="mt-2 text-xl text-text-secondary sm:text-2xl" data-id="element-407">
                  {currentMaterial?.description}
                </p>
              </div>

              <div className="w-full max-w-3xl overflow-hidden rounded-2xl" data-id="element-408">
                <video src="https://cdn.magicpatterns.com/uploads/o5o7GMaCBNRzUVHHAmZcq3/Unbenannt%20-%2011.%20Juli%202026%20um%2021.52.31%20(2).mp4" autoPlay loop muted playsInline className="w-full" style={{
              mixBlendMode: 'multiply'
            }} data-id="element-409" />
              </div>
            </motion.div> : <motion.div key="weighing" className="mx-auto mt-6 flex w-full max-w-5xl flex-col rounded-2xl border border-border bg-surface shadow-sm sm:mt-8" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2,
          ease: 'easeOut'
        }} data-id="element-410">
              <div className="p-5 sm:p-7" data-id="element-411">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" data-id="element-412">
                  <div className="flex items-center gap-2.5 text-text-secondary" data-id="element-413">
                    <span className="font-heading text-2xl" data-id="element-414">Waage 1</span>
                  </div>
                  <div className="text-left sm:text-right" data-id="element-415">
                    <div className="font-heading text-lg text-text-primary sm:text-xl" data-id="element-416">
                      {currentMaterial?.articleNumber}
                    </div>
                    <div className="mt-0.5 text-base text-text-secondary" data-id="element-417">
                      {currentMaterial?.description}
                    </div>
                  </div>
                </div>

                <div className="mt-5 flex w-full items-center gap-4 rounded-xl bg-brand-yellow-light px-5 py-4 text-left" data-id="element-418">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface text-text-primary" data-id="element-419">
                    <InfoIcon size={21} strokeWidth={2.4} aria-hidden="true" data-id="element-420" />
                  </div>
                  <p className="text-lg leading-7 text-text-primary sm:text-xl" data-id="element-421">
                    Achtung: Rohstoff staubt stark. Langsam und bodennah
                    zugeben.
                  </p>
                </div>

                <div className="mt-5 text-center" data-id="element-422">
                  <div className="text-sm font-medium uppercase tracking-wider text-text-secondary" data-id="element-423">
                    Soll-Gewicht
                  </div>
                  <div className="mt-1 font-heading text-5xl text-text-primary sm:text-6xl" data-id="element-424">
                    {targetWeight.toLocaleString('de-DE', {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1
                })}{' '}
                    kg
                  </div>
                </div>

                <div className={`relative mt-6 rounded-xl border p-5 text-center transition-colors sm:p-7 ${isWeightReached ? 'border-success bg-success text-surface' : 'border-border bg-surface-subtle'}`} role="status" aria-live="polite" data-id="element-425">
                  <button ref={scaleServiceTriggerRef} type="button" onClick={() => setIsScaleServiceOpen(true)} aria-haspopup="dialog" aria-expanded={isScaleServiceOpen} aria-label="Höfelmeyer-Serviceinformationen für Waage 1 öffnen" className="absolute right-2 top-2 rounded-md p-1 transition-transform hover:scale-105 focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green" data-id="element-426">
                    <img src="https://cdn.magicpatterns.com/uploads/2dVynaPzVDgou8WbPkapLR/HofelmeyerLogo_(1)_(3).png" alt="" aria-hidden="true" className={`h-11 w-auto max-w-[205px] object-contain sm:h-12 ${isWeightReached ? '[filter:brightness(0)_invert(1)]' : ''}`} data-id="element-427" />
                  </button>
                  <div className={`text-sm font-medium uppercase tracking-wider ${isWeightReached ? 'text-surface' : 'text-text-secondary'}`} data-id="element-428">
                    Ist-Gewicht
                  </div>
                  <div className={`mt-2 font-heading text-6xl sm:text-7xl ${isWeightReached ? 'text-surface' : 'text-text-primary'}`} data-id="element-429">
                    {displayWeight.toLocaleString('de-DE', {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1
                })}
                    <span className={`ml-2 text-4xl sm:text-5xl ${isWeightReached ? 'text-surface' : 'text-text-secondary'}`} data-id="element-430">
                      kg
                    </span>
                  </div>
                  {isWeightReached ? <div className="mt-4 flex items-center justify-center gap-2 font-heading text-lg" data-id="element-431">
                      <CheckCircle2Icon size={24} strokeWidth={2.4} aria-hidden="true" data-id="element-432" />
                      Sollgewicht erreicht
                    </div> : null}
                  {tareWeight > 0 && <div className={`mt-3 text-base ${isWeightReached ? 'text-surface' : 'text-text-secondary'}`} data-id="element-433">
                      Tara:{' '}
                      {tareWeight.toLocaleString('de-DE', {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1
                })}{' '}
                      kg
                    </div>}
                </div>

                <div className="mt-6 grid grid-cols-2 gap-3 sm:gap-4" data-id="element-434">
                  <button type="button" onClick={handleZero} className="min-h-[56px] rounded-lg border border-border bg-surface font-heading text-lg text-text-primary transition-colors hover:bg-surface-subtle sm:min-h-[64px] sm:text-xl" data-id="element-435">
                    Nullstellen
                  </button>
                  <button type="button" onClick={handleTare} className="min-h-[56px] rounded-lg border border-border bg-surface font-heading text-lg text-text-primary transition-colors hover:bg-surface-subtle sm:min-h-[64px] sm:text-xl" data-id="element-436">
                    Tarieren
                  </button>
                </div>
              </div>

              <div className="border-t border-border px-5 pb-3 pt-3 sm:px-7 sm:pb-4 sm:pt-4" data-id="element-437">
                <button type="button" onClick={handleConfirmWeight} disabled={!isWeightReached} className="flex min-h-[64px] w-full items-center justify-center gap-3 rounded-lg bg-brand-green font-heading text-xl text-text-primary transition-colors hover:bg-brand-green-dark disabled:bg-surface-subtle disabled:text-text-secondary sm:min-h-[76px] sm:text-2xl" data-id="element-438">
                  <CheckIcon size={28} strokeWidth={2} data-id="element-439" />
                  Gewicht bestätigen
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </motion.div>

      <Modal open={isScaleServiceOpen} onOpenChange={open => {
      if (!open) closeScaleService();
    }} title="Serviceinformationen · Waage 1" description="Technische Daten und direkter Kontakt zum Höfelmeyer-Service." size="lg" closeLabel="Serviceinformationen schließen" footer={<button type="button" onClick={closeScaleService} className="min-h-[52px] rounded-lg bg-brand-green px-6 font-heading text-base text-text-primary transition-colors hover:bg-brand-green-dark focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green" data-id="element-441">
            Schließen
          </button>} data-id="element-440">
        <div className="grid gap-6" data-id="element-442">
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-id="element-443">
            <div className="rounded-xl border border-border bg-surface-subtle p-4" data-id="element-444">
              <dt className="text-xs font-medium uppercase tracking-wider text-text-secondary" data-id="element-445">
                SRA-Nr.
              </dt>
              <dd className="mt-1 font-heading text-lg text-text-primary" data-id="element-446">
                SRA-2407-001
              </dd>
            </div>
            <div className="rounded-xl border border-border bg-surface-subtle p-4" data-id="element-447">
              <dt className="text-xs font-medium uppercase tracking-wider text-text-secondary" data-id="element-448">
                Gerät
              </dt>
              <dd className="mt-1 font-heading text-lg text-text-primary" data-id="element-449">
                HTF60E
              </dd>
            </div>
            <div className="rounded-xl border border-border bg-surface-subtle p-4" data-id="element-450">
              <dt className="text-xs font-medium uppercase tracking-wider text-text-secondary" data-id="element-451">
                Software
              </dt>
              <dd className="mt-1 font-heading text-lg text-text-primary" data-id="element-452">
                CONTROLE ONLINE E
              </dd>
            </div>
            <div className="rounded-xl border border-border bg-surface-subtle p-4" data-id="element-453">
              <dt className="text-xs font-medium uppercase tracking-wider text-text-secondary" data-id="element-454">
                Firmware
              </dt>
              <dd className="mt-1 font-heading text-lg text-text-primary" data-id="element-455">
                2.16.4
              </dd>
            </div>
          </dl>

          <section className="overflow-hidden rounded-xl border border-border bg-surface" aria-labelledby="service-contact-title" data-id="element-456">
            <div className="p-5" data-id="element-457">
              <p className="text-xs font-medium uppercase tracking-wider text-text-secondary" data-id="element-458">
                Höfelmeyer Service
              </p>
              <h3 id="service-contact-title" className="mt-1 font-heading text-2xl text-text-primary" data-id="element-459">
                André Schimmöller
              </h3>
              <p className="mt-1 text-base text-text-secondary" data-id="element-460">
                Stellvertretender Serviceleiter
              </p>
              <div className="mt-5 grid gap-2 text-base" data-id="element-461">
                <a href="tel:+495401497777" className="flex w-fit items-center gap-2 text-text-primary underline decoration-border underline-offset-4 transition-colors hover:text-brand-green-dark focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green" data-id="element-462">
                  <PhoneIcon size={18} aria-hidden="true" data-id="element-463" />
                  +49 5401 4977 77
                </a>
                <a href="tel:+491758282694" className="flex w-fit items-center gap-2 text-text-primary underline decoration-border underline-offset-4 transition-colors hover:text-brand-green-dark focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green" data-id="element-464">
                  <PhoneIcon size={18} aria-hidden="true" data-id="element-465" />
                  +49 175 8282694
                </a>
                <a href="mailto:a.schimmoeller@hoefelmeyer.de" className="flex w-fit items-center gap-2 break-all text-text-primary underline decoration-border underline-offset-4 transition-colors hover:text-brand-green-dark focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green" data-id="element-466">
                  <MailIcon size={18} aria-hidden="true" data-id="element-467" />
                  a.schimmoeller@hoefelmeyer.de
                </a>
              </div>
            </div>
          </section>
        </div>
      </Modal>

      <div className="group fixed bottom-0 right-0 z-50 flex h-32 w-32 items-end justify-end p-4" data-id="element-468">
        <div className="flex flex-col gap-2 opacity-0 transition-opacity duration-300 group-hover:opacity-100" data-id="element-469">
          <button type="button" onClick={() => handleSimulateScan('success')} className="rounded-md bg-success px-3 py-2 text-xs font-medium text-surface shadow-sm hover:bg-success/90" data-id="element-470">
            Richtig scannen
          </button>
          <button type="button" onClick={() => handleSimulateScan('error')} className="rounded-md bg-error px-3 py-2 text-xs font-medium text-surface shadow-sm hover:bg-error/90" data-id="element-471">
            Falsch scannen
          </button>
        </div>
      </div>

      <AnimatePresence data-id="element-472">
        {isOrderCompletionFeedbackOpen && <motion.section role="status" aria-live="assertive" aria-atomic="true" className="fixed inset-0 z-[70] flex items-center justify-center bg-success p-6 text-center text-surface" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: prefersReducedMotion ? 0 : 0.2,
        ease: 'easeOut'
      }} data-id="element-473">
            <motion.div className="w-full max-w-xl" initial={{
          opacity: 0,
          y: prefersReducedMotion ? 0 : 14
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: prefersReducedMotion ? 0 : -8
        }} transition={{
          duration: prefersReducedMotion ? 0 : 0.28,
          ease: 'easeOut'
        }} data-id="element-474">
              <motion.div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full border-4 border-surface/40 bg-surface text-success" initial={{
            opacity: 0,
            scale: prefersReducedMotion ? 1 : 0.86
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: prefersReducedMotion ? 0 : 0.3,
            delay: prefersReducedMotion ? 0 : 0.08,
            ease: 'easeOut'
          }} aria-hidden="true" data-id="element-475">
                <CheckCircle2Icon size={58} strokeWidth={2} data-id="element-476" />
              </motion.div>
              <p className="mt-8 text-sm font-semibold uppercase tracking-[0.18em] text-surface/80" data-id="element-477">
                Auftrag abgeschlossen
              </p>
              <h2 className="mt-3 font-heading text-4xl leading-tight sm:text-5xl" data-id="element-478">
                {order.product}
              </h2>
              <p className="mt-4 text-lg leading-7 text-surface sm:text-xl" data-id="element-479">
                {order.id} · {order.lots} Lots vollständig verwogen.
              </p>
              <p className="mt-6 text-sm text-surface/80" data-id="element-480">
                Sie kehren automatisch zur Auftragsliste zurück.
              </p>
            </motion.div>
          </motion.section>}

        {isWeightChangeConfirmationOpen && <motion.div className="fixed inset-0 z-[60] flex items-end justify-center bg-text-primary/50 p-4 backdrop-blur-sm sm:items-center sm:p-6" role="presentation" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onMouseDown={closeWeightChangeConfirmation} data-id="element-481">
            <motion.section role="dialog" aria-modal="true" aria-labelledby="weight-change-confirmation-title" aria-describedby="weight-change-confirmation-description" tabIndex={-1} onKeyDown={event => {
          if (event.key === 'Escape') {
            closeWeightChangeConfirmation();
          }
        }} className="w-full max-w-2xl rounded-2xl border border-border bg-surface p-6 shadow-md sm:p-8" initial={{
          opacity: 0,
          y: 16,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 16,
          scale: 0.98
        }} transition={{
          duration: 0.2,
          ease: 'easeOut'
        }} onMouseDown={event => event.stopPropagation()} data-id="element-482">
              <h2 id="weight-change-confirmation-title" className="font-heading text-3xl leading-tight" data-id="element-483">
                Gewichtsänderungen übernehmen?
              </h2>
              <p id="weight-change-confirmation-description" className="mt-2 text-base leading-6 text-text-secondary" data-id="element-484">
                Die folgenden Sollmengen werden für den restlichen Auftrag{' '}
                <span className="font-heading text-text-primary" data-id="element-485">
                  {order.id}
                </span>{' '}
                übernommen.
              </p>

              <ul className="mt-6 space-y-3" aria-label="Geänderte Rohstoffgewichte" data-id="element-486">
                {changedMaterialQuantities.map(material => <li key={material.articleNumber} className="rounded-xl border border-border bg-surface-subtle p-4" data-id="element-487">
                    <p className="font-heading text-lg text-text-primary" data-id="element-488">
                      {material.articleNumber}
                    </p>
                    <p className="mt-1 text-sm text-text-secondary" data-id="element-489">
                      {material.description}
                    </p>
                    <p className="mt-3 font-heading text-lg text-text-primary" data-id="element-490">
                      {formatKilograms(material.previousQuantityKg)} kg{' '}
                      <span aria-hidden="true" data-id="element-491">→</span>{' '}
                      {formatKilograms(material.nextQuantityKg)} kg
                    </p>
                  </li>)}
              </ul>

              <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end" data-id="element-492">
                <button type="button" onClick={closeWeightChangeConfirmation} className="min-h-[60px] rounded-xl border border-border bg-surface px-7 font-heading text-lg text-text-primary transition-colors hover:bg-surface-subtle focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green" data-id="element-493">
                  Zurück
                </button>
                <button type="button" onClick={saveMaterialChanges} className="min-h-[60px] rounded-xl bg-brand-green px-8 font-heading text-lg text-text-primary transition-colors hover:bg-brand-green-dark focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green" data-id="element-494">
                  Fortfahren
                </button>
              </div>
            </motion.section>
          </motion.div>}

        {isSupervisorCodeOpen && <motion.div className="fixed inset-0 z-50 flex items-end justify-center bg-text-primary/50 p-4 backdrop-blur-sm sm:items-center sm:p-6" role="presentation" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onMouseDown={closeSupervisorCode} data-id="element-495">
            <motion.section role="dialog" aria-modal="true" aria-labelledby="supervisor-code-title" aria-describedby="supervisor-code-description" className="w-full max-w-xl rounded-2xl border border-border bg-surface p-6 shadow-md sm:p-8" initial={{
          opacity: 0,
          y: 16,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 16,
          scale: 0.98
        }} transition={{
          duration: 0.2,
          ease: 'easeOut'
        }} onMouseDown={event => event.stopPropagation()} data-id="element-496">
              {isSupervisorApproved ? <div className="py-8 text-center" role="status" aria-live="polite" data-id="element-497">
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-success text-surface" data-id="element-498">
                    <CheckIcon size={32} strokeWidth={2.5} aria-hidden="true" data-id="element-499" />
                  </div>
                  <h2 id="supervisor-code-title" className="mt-5 font-heading text-3xl" data-id="element-500">
                    Freigabe bestätigt
                  </h2>
                  <p className="mt-2 text-base text-text-secondary" data-id="element-501">
                    Der Supervisor-Code wurde akzeptiert.
                  </p>
                </div> : <form onSubmit={handleSupervisorApproval} data-id="element-502">
                  <h2 id="supervisor-code-title" className="font-heading text-3xl leading-tight" data-id="element-503">
                    Supervisor-Freigabe
                  </h2>
                  <p id="supervisor-code-description" className="mt-2 text-base leading-6 text-text-secondary" data-id="element-504">
                    Geben Sie den sechsstelligen Code ein, um Änderungen an der
                    Rohstoffübersicht freizugeben.
                  </p>

                  <fieldset className="mt-7" data-id="element-505">
                    <legend className="font-heading text-base text-text-primary" data-id="element-506">
                      Supervisor-Code
                    </legend>
                    <p className="mt-1 text-sm text-text-secondary" data-id="element-507">
                      Bitte geben Sie alle sechs Ziffern ein.
                    </p>

                    <div className="mt-4 grid w-full grid-cols-6 gap-2 sm:gap-3" role="group" aria-describedby={supervisorCodeError ? 'supervisor-code-error' : undefined} data-id="element-508">
                      {Array.from({
                  length: 6
                }, (_, index) => {
                  const digit = supervisorCode[index] ?? '';
                  return <input key={index} ref={index === 0 ? supervisorCodeInputRef : undefined} id={`supervisor-code-${index + 1}`} aria-label={`Ziffer ${index + 1} des Supervisor-Codes`} type="password" inputMode="numeric" autoComplete={index === 0 ? 'one-time-code' : 'off'} maxLength={1} value={digit} onChange={event => {
                    const nextDigit = event.target.value.replace(/\D/g, '').slice(-1);
                    const nextCode = Array.from({
                      length: 6
                    }, (_, codeIndex) => codeIndex === index ? nextDigit : supervisorCode[codeIndex] ?? '').join('');
                    handleSupervisorCodeChange(nextCode);
                    if (nextDigit && index < 5) {
                      document.getElementById(`supervisor-code-${index + 2}`)?.focus();
                    }
                  }} onKeyDown={event => {
                    if (event.key === 'Backspace' && !digit && index > 0) {
                      document.getElementById(`supervisor-code-${index}`)?.focus();
                    }
                  }} onPaste={event => {
                    const pastedCode = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
                    if (!pastedCode) {
                      return;
                    }
                    event.preventDefault();
                    handleSupervisorCodeChange(pastedCode);
                    document.getElementById(`supervisor-code-${Math.min(pastedCode.length + 1, 6)}`)?.focus();
                  }} className="aspect-square min-w-0 w-full rounded-xl border border-transparent bg-surface-subtle text-center font-heading text-3xl text-text-primary outline-none transition-colors hover:border-border focus:border-brand-green focus:bg-surface focus:ring-2 focus:ring-brand-green/25 sm:text-4xl" required={index === 0} data-id="element-509" />;
                })}
                    </div>

                    {supervisorCodeError ? <p id="supervisor-code-error" className="mt-3 text-sm font-medium text-error" role="alert" data-id="element-510">
                        {supervisorCodeError}
                      </p> : null}
                  </fieldset>

                  <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end" data-id="element-511">
                    <button type="button" onClick={closeSupervisorCode} className="min-h-[60px] rounded-xl border border-border bg-surface px-7 font-heading text-lg text-text-primary transition-colors hover:bg-surface-subtle focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green" data-id="element-512">
                      Abbrechen
                    </button>
                    <button type="submit" className="min-h-[60px] rounded-xl bg-brand-green px-8 font-heading text-lg text-text-primary transition-colors hover:bg-brand-green-dark focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green" data-id="element-513">
                      Bestätigen
                    </button>
                  </div>
                </form>}
            </motion.section>
          </motion.div>}

        {scanMessage && <motion.div key={scanMessage.text} role="status" aria-live="polite" aria-atomic="true" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className={`fixed inset-0 z-50 flex flex-col items-center justify-center p-6 text-center ${scanMessage.type === 'success' ? 'bg-success text-surface' : 'bg-error text-surface'}`} data-id="element-514">
            {scanMessage.type === 'success' ? <CheckCircle2Icon size={120} strokeWidth={1.5} aria-hidden="true" data-id="element-515" /> : <CircleAlertIcon size={120} strokeWidth={1.5} aria-hidden="true" data-id="element-516" />}
            <h2 className="mt-8 font-heading text-5xl leading-tight sm:text-6xl" data-id="element-517">
              {scanMessage.text}
            </h2>
          </motion.div>}
      </AnimatePresence>
    </section>;
}
function getFirstOpenMaterialIndex(materials: OrderMaterial[], weighedMaterialIds: string[]) {
  const openMaterialIndex = materials.findIndex(material => !weighedMaterialIds.includes(material.articleNumber));
  return openMaterialIndex === -1 ? 0 : openMaterialIndex;
}
function formatKilograms(weightKg: number) {
  return weightKg.toLocaleString('de-DE', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  });
}